/**
 * RequirePlatformAdmin Route Guard (#670)
 *
 * Wraps routes that require platform administrator access (Agion staff only).
 * Non-admin users are redirected to /access-denied, which receives the
 * requested page as `next` so that an organization switch can return to it.
 *
 * Usage:
 *   <Route path="/hosts" element={
 *     <RequirePlatformAdmin>
 *       <InfraHostsListPage />
 *     </RequirePlatformAdmin>
 *   } />
 */

import { Navigate, useLocation } from 'react-router'
import { Spinner } from '@agion/ui'
import { usePlatformAdmin } from '@/hooks/infra_use_platform_admin'
import { accessDeniedPath } from '@/core/infra_access_denied'

interface RequirePlatformAdminProps {
  children: React.ReactNode
}

export function RequirePlatformAdmin({ children }: RequirePlatformAdminProps) {
  const { isPlatformAdmin, isLoading } = usePlatformAdmin()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!isPlatformAdmin) {
    const requested = `${location.pathname}${location.search}${location.hash}`
    return <Navigate to={accessDeniedPath(requested, 'platform-admin')} replace />
  }

  return <>{children}</>
}
